// ============================================================
// caseHelpers.ts
// Utility functions used across the application
// ============================================================
import { Case, CaseControl } from '../types/case.types'
import { COMPLETABLE_SECTIONS } from '../store/useSectionCompletion'
import { caseRef } from './caseName'
export const isIncomplete = (value: string | null | undefined): boolean =>
  value === null || value === undefined || value === ''
export const getCompletedCaseControlFields = (
  caseControl: CaseControl
): Partial<CaseControl> => {
  const result: Partial<CaseControl> = {}
  if (!isIncomplete(caseControl.handover))  result.handover  = caseControl.handover
  if (!isIncomplete(caseControl.openDate))  result.openDate  = caseControl.openDate
  if (!isIncomplete(caseControl.closeDate)) result.closeDate = caseControl.closeDate
  return result
}
// ★ Date-only values ('YYYY-MM-DD', or a UTC-midnight timestamp — how
// accident dates, DOBs and deadlines are stored) are CIVIL dates: format them
// in UTC so they show the same day in every timezone. (In a negative-offset
// browser, local formatting showed the PREVIOUS day.) Real timestamps (e.g.
// updatedAt) keep local-time formatting.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}(T00:00:00(\.000)?Z)?$/
export const formatDateUK = (isoDate: string | null): string => {
  if (!isoDate) return '—'
  const d = new Date(isoDate)
  if (Number.isNaN(d.getTime())) return '—'
  return DATE_ONLY.test(isoDate)
    ? d.toLocaleDateString('en-GB', { timeZone: 'UTC' })
    : d.toLocaleDateString('en-GB')
}
// Civil 'YYYY-MM-DD' + N years, with NO timezone involvement. A 29 Feb that
// doesn't exist in the target year clamps to 28 Feb (never rolls to 1 Mar).
// Returns 'YYYY-MM-DD', or null for an invalid input.
export const addYearsCivil = (ymd: string, years: number): string | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd)
  if (!m) return null
  const y = Number(m[1]) + years
  const mo = Number(m[2])
  let d = Number(m[3])
  const daysInMonth = new Date(Date.UTC(y, mo, 0)).getUTCDate()
  if (d > daysInMonth) d = daysInMonth
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}
// The case-page reference — delegates to THE shared caseRef() (SURNAME-YEAR-ID,
// utils/caseName.ts), fed the editable snapshot surname (live as the user types)
// with the live Client as fallback. No surname → "DRAFT-<year>-<id>".
export const getCaseReference = (caseData: Case): string =>
  caseRef({ id: caseData.id, createdAt: caseData.createdAt, clientSurname: caseData.clientDetails.surname, client: caseData.client })
// Calculates case progress as a percentage of completed sequential
// sections. Backed by the per-case completedSections String[] column
// (added P4 backend), which is the same data useSectionCompletion
// writes via markComplete/markIncomplete. Uses COMPLETABLE_SECTIONS
// from the store as the canonical denominator so the two stay in
// sync if sections are added or removed.
//
// caseData.completedSections may be undefined for two reasons today:
//   - pre-P4-backend rows that haven't been touched since the column
//     was added (Reuben's migration backfills, but defensive treatment)
//   - the frontend CaseSummary type declares the field as optional
//     because not every row guarantees it server-side yet
// Either way, undefined → 0 → 0% progress, which is what we want.
export const calculateCaseCompletion = (caseData: { completedSections?: string[] }): number => {
  const completed = caseData.completedSections?.length ?? 0
  return Math.round((completed / COMPLETABLE_SECTIONS.length) * 100)
}
